import { Box, Stack } from '@ltvco/refresh-lib/theme';
import { type RefObject } from 'react';

import {
  NavChildIndicator,
  NavChildItem,
  NavChildLabel,
  NavContainer,
  NavGroupLabel,
  NavLeafItem,
  NavOverviewLink,
} from './components';
import { useActiveSection } from './hooks';

import type { NavSection } from './navConfig';

interface ReportSideNavProps {
  sections: NavSection[];
  scrollContainerRef: RefObject<HTMLDivElement>;
}

export const ReportSideNav = ({
  sections,
  scrollContainerRef,
}: ReportSideNavProps) => {
  const activeHref = useActiveSection(sections, scrollContainerRef);

  const scrollToTop = () =>
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const scrollTo = (href: string) => {
    const target = scrollContainerRef.current?.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isActive = (href: string) => activeHref === href;
  const isGroupActive = (section: NavSection) =>
    isActive(section.href) ||
    (section.children?.some((c) => isActive(c.href)) ?? false);

  return (
    <NavContainer>
      <NavOverviewLink variant="body2" onClick={scrollToTop}>
        Overview
      </NavOverviewLink>

      <Stack spacing={2.5}>
        {sections.map((section) => (
          <Box key={section.href}>
            {section.children ? (
              <Box>
                <NavGroupLabel
                  variant="body2"
                  isActive={isGroupActive(section)}
                  onClick={() => scrollTo(section.href)}
                >
                  {section.label}
                </NavGroupLabel>
                <Box sx={{ mt: 0.5 }}>
                  {section.children.map((child) => (
                    <NavChildItem
                      key={child.href}
                      onClick={() => scrollTo(child.href)}
                    >
                      <NavChildIndicator isActive={isActive(child.href)} />
                      <NavChildLabel
                        className="nav-child-text"
                        variant="body2"
                        isActive={isActive(child.href)}
                      >
                        {child.label}
                      </NavChildLabel>
                    </NavChildItem>
                  ))}
                </Box>
              </Box>
            ) : (
              <NavLeafItem
                variant="body2"
                isActive={isActive(section.href)}
                onClick={() => scrollTo(section.href)}
              >
                {section.label}
              </NavLeafItem>
            )}
          </Box>
        ))}
      </Stack>
    </NavContainer>
  );
};
